// Header bell for the logged-in user's notifications. The unread badge polls
// every 30s; the dropdown (styled after SearchBox's result list) loads the 20
// most recent rows when opened. Clicking a row marks it read then navigates;
// "Mark all read" clears the rest server-side in one bulk mutation.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { gqlRequest } from "../lib/gql";
import {
  MARK_ALL_NOTIFICATIONS_READ,
  MARK_NOTIFICATION_READ,
  NOTIFICATIONS,
  UNREAD_NOTIFICATION_COUNT,
  type NotificationNode,
  type NotificationsResult,
  type UnreadNotificationCountResult,
} from "../lib/queries";
import { formatRelativeTime } from "../lib/format";
import { useT } from "../lib/i18n";
import { Avatar } from "./Avatar";

const KIND_KEY = {
  OUTING_LIKE: "notif.OUTING_LIKE",
  OUTING_COMMENT: "notif.OUTING_COMMENT",
  PERSON_FOLLOW: "notif.PERSON_FOLLOW",
  CLUB_INVITE: "notif.CLUB_INVITE",
  CLUB_JOIN_REQUEST: "notif.CLUB_JOIN_REQUEST",
  CLUB_JOIN_APPROVED: "notif.CLUB_JOIN_APPROVED",
  EVENT_INVITE: "notif.EVENT_INVITE",
} as const;

function notificationHref(n: NotificationNode): string {
  if (n.kind === "PERSON_FOLLOW") return `/person/${n.actorId}`;
  // Club/event rows keep working even when the id is unexpectedly null —
  // Discover is the closest useful landing page.
  if (n.kind === "CLUB_INVITE" || n.kind === "CLUB_JOIN_REQUEST" || n.kind === "CLUB_JOIN_APPROVED")
    return n.clubId ? `/group/${n.clubId}` : "/discover";
  if (n.kind === "EVENT_INVITE") return n.eventId ? `/event/${n.eventId}` : "/discover?tab=events";
  return n.outingId ? `/outing/${n.outingId}` : `/person/${n.actorId}`;
}

export function NotificationBell() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const unread = useQuery({
    queryKey: ["notifications-unread"],
    refetchInterval: 30_000,
    queryFn: () => gqlRequest<UnreadNotificationCountResult>(UNREAD_NOTIFICATION_COUNT),
  });
  const unreadCount = unread.data?.notifications.totalCount ?? 0;

  const list = useQuery({
    queryKey: ["notifications"],
    enabled: open,
    queryFn: () => gqlRequest<NotificationsResult>(NOTIFICATIONS),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
  };

  const markOne = useMutation({
    mutationFn: (id: string) =>
      gqlRequest(MARK_NOTIFICATION_READ, { id, readAt: new Date().toISOString() }),
    onSettled: invalidate,
  });

  const markAll = useMutation({
    mutationFn: () => gqlRequest(MARK_ALL_NOTIFICATIONS_READ, { readAt: new Date().toISOString() }),
    onSettled: invalidate,
  });

  const onSelect = (n: NotificationNode) => {
    // Fire-and-forget: navigation must not wait on the write; invalidation in
    // onSettled refreshes the badge whenever the mutation lands.
    if (!n.readAt) markOne.mutate(n.id);
    setOpen(false);
    navigate(notificationHref(n));
  };

  const nodes = list.data?.notifications.nodes ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={unreadCount > 0 ? t("notif.ariaCount", { count: unreadCount }) : t("notif.title")}
          className="relative rounded-lg text-muted-foreground hover:text-foreground"
        >
          <Bell aria-hidden className="size-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-4 text-primary-foreground">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="z-40 max-h-96 w-80 overflow-y-auto rounded-xl p-0 shadow-pop">
        <div className="sticky top-0 flex items-center justify-between border-b bg-muted/90 px-3 py-1.5 backdrop-blur-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("notif.title")}</p>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => markAll.mutate()}
              disabled={markAll.isPending}
              className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
            >
              {markAll.isPending ? t("notif.marking") : t("notif.markAll")}
            </button>
          )}
        </div>

        {list.isLoading && <p className="p-3.5 text-sm text-muted-foreground">{t("common.loading")}</p>}
        {list.error != null && <p className="p-3.5 text-sm text-destructive">{t("notif.loadError")}</p>}
        {list.data && nodes.length === 0 && (
          <p className="p-3.5 text-sm text-muted-foreground">{t("notif.empty")}</p>
        )}

        {nodes.map((n) => {
          const isUnread = n.readAt == null;
          return (
            <button
              key={n.id}
              type="button"
              onClick={() => onSelect(n)}
              className={cn(
                "block w-full border-b px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-accent",
                isUnread && "bg-primary/5",
              )}
            >
              <span className="flex items-start gap-2">
                {/* Unread dot column keeps text aligned across read/unread rows */}
                <span
                  aria-hidden
                  className={cn("mt-1.5 size-2 shrink-0 rounded-full", isUnread ? "bg-primary" : "bg-transparent")}
                />
                <Avatar
                  person={{ displayName: n.actor?.displayName ?? null, avatarKey: n.actor?.avatarKey }}
                  size="sm"
                  className="mt-0.5"
                />
                <span className="min-w-0">
                  <span className={cn("block text-sm text-foreground", isUnread && "font-semibold")}>
                    {n.actor?.displayName ?? t("common.someone")} {t(KIND_KEY[n.kind])}
                  </span>
                  {/* The kind text stays generic ("…a group"); this line names
                      the target when RLS lets the recipient see it. */}
                  {(n.outing?.title ?? n.club?.name ?? n.event?.title) && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {n.outing?.title ?? n.club?.name ?? n.event?.title}
                    </span>
                  )}
                  <span className="block text-xs text-muted-foreground">{formatRelativeTime(n.createdAt)}</span>
                </span>
              </span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
